// Tenantra Inc. - Payment habits: how tenants pay, auto-pay adoption and rent through Tenantra vs collected outside (last 12 months)
import api from '@/services/api';
import { Card, money, pct, plural } from './kit';

function Bar({ label, value, share, color, testId, sub }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-baseline justify-between text-xs"><span className="text-slate-600">{label}</span><span className="text-slate-800 tabular-nums">{value}</span></div>
      <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className={`h-full rounded-full ${color}`} style={{ width: `${Math.max(Math.min(share, 100), share > 0 ? 2 : 0)}%` }} /></div>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export function PaymentHabits({ habits, scope, onDrill }) {
  const { methods, autopay, channel, returned } = habits;
  const paidTotal = methods.reduce((s, m) => s + m.amount, 0);
  const viaShare = channel.tenantra + channel.outside ? (channel.tenantra / (channel.tenantra + channel.outside)) * 100 : 0;
  const drillAuto = () => onDrill({
    crumb: 'Auto-pay', title: 'Auto-pay by tenant', showTotal: false,
    load: () => api.get('/landlord/analytics/drill', { params: { kind: 'tenancies', ...scope } }).then(r => r.data.rows.sort((a, b) => b.autopay - a.autopay).map(t => ({
      key: t.tenancy_id, tenant_id: t.tenant_id, primary: t.tenant, secondary: `${t.where} · ${money(t.rent)}/mo`, value: t.autopay ? 'On' : 'Off', tone: t.autopay ? 'text-teal-700' : 'text-slate-400',
    }))),
  });
  return (
    <Card title="Payment habits" aside="Last 12 months" testId="payment-habits">
      <div className="px-5 pb-4 space-y-4">
        <button type="button" onClick={drillAuto} className="w-full text-left rounded-md -mx-2 px-2 py-1.5 hover:bg-slate-50/80 transition-colors" data-testid="habits-autopay">
          <p className="text-xs text-slate-500">Tenants on auto-pay</p>
          <p className="text-lg font-medium text-slate-800 tabular-nums">{pct(autopay.pct)} <span className="text-xs font-normal text-slate-500">{autopay.on} of {plural(autopay.of, 'active tenant')}</span></p>
        </button>
        <div className="space-y-2.5" data-testid="habits-methods">
          <p className="text-xs text-slate-500">Paid through Tenantra, by method</p>
          {methods.length === 0 ? <p className="text-xs text-slate-400">No payments through Tenantra yet.</p>
            : methods.map(m => <Bar key={m.method} label={m.label} value={money(m.amount)} share={(m.amount / (paidTotal || 1)) * 100} color="bg-teal-600" sub={plural(m.count, 'payment')} testId={`habits-method-${m.method}`} />)}
        </div>
        <div className="space-y-2.5" data-testid="habits-channel">
          <p className="text-xs text-slate-500">Rent received</p>
          <Bar label="Through Tenantra" value={money(channel.tenantra)} share={viaShare} color="bg-teal-600" testId="habits-channel-tenantra" />
          <Bar label="Recorded by you (paid outside)" value={money(channel.outside)} share={100 - viaShare} color="bg-slate-400" testId="habits-channel-outside" />
        </div>
        {returned > 0 && <p className="text-xs text-red-700" data-testid="habits-returned">{plural(returned, 'payment')} returned or failed</p>}
      </div>
    </Card>
  );
}
